import moment from 'moment';

import type { NMonthlyCalendar } from '@app/components/Calendars/MonthlyCalendar/types';
import type { NCalendars } from '@app/components/Calendars/types';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';

export const generateWeeks = (
  date: NMonthlyCalendar.MonthType
): NMonthlyCalendar.Weeks => {
  const startDate = moment(`${date.year}-${date.month}-01`, 'YYYY-MM-DD');
  const endDate = startDate.clone().endOf('month');
  const today = moment();
  const startCalendarDate = startDate.clone().startOf('week');
  const endCalendarDate = endDate.clone().endOf('week');
  const currentDay = startCalendarDate.clone();
  const weeks: NMonthlyCalendar.Weeks = [];

  while (currentDay.isBefore(endCalendarDate)) {
    const weekNumber = currentDay.week();
    const weekId = `${date.year}-${date.month}-${weekNumber}`;
    const week: NMonthlyCalendar.WeekType = {
      id: weekId,
      days: [],
    };

    for (let i = 0; i < 7; i++) {
      const dayId = `${currentDay.year()}-${
        currentDay.month() + 1
      }-${currentDay.date()}`;
      week.days.push({
        id: dayId,
        day: currentDay.date(),
        isCurrentMonth: currentDay.month() + 1 === date.month,
        isToday: currentDay.isSame(today, 'day'),
        weekDayNumber: i + 1,
      });
      currentDay.add(1, 'day');
    }

    weeks.push(week);
  }

  return weeks;
};

export const getDuration = ({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}): number => {
  const start = moment(startTime).startOf('day');
  const end = moment(endTime).startOf('day');
  return end.diff(start, 'days') + 1;
};

export const sortTasksByDuration = (
  tasks: NMonthlyCalendar.TaskType
): NMonthlyCalendar.TaskType => {
  return tasks.sort((a, b) => {
    const aDuration = getDuration(a);
    const bDuration = getDuration(b);
    if (aDuration !== bDuration) {
      return bDuration - aDuration;
    }

    return moment(a.startTime).diff(moment(b.startTime));
  });
};

export const findFreePosition = (
  tasksMap: Map<string, Array<NMonthlyCalendar.UpdateTaskInterface>>,
  task: NCalendars.TaskInterface
): number => {
  let position = 1;
  const startMoment = moment(task.startTime);
  const endMoment = moment(task.endTime);
  let isPositionFound = false;

  while (!isPositionFound) {
    let isPositionFree = true;
    for (
      let m = startMoment.clone();
      m.isSameOrBefore(endMoment, 'day');
      m.add(1, 'days')
    ) {
      const currentDay = m.date().toString();
      const currentMonth = m.month() + 1;
      const dayTasks = tasksMap.get(`${currentMonth}-${currentDay}`) || [];
      if (dayTasks.some((t) => t.position === position)) {
        isPositionFree = false;
        break;
      }
    }

    if (isPositionFree) {
      isPositionFound = true;
    } else {
      position++;
    }
  }

  return position;
};

export const formatMonthlyTasks = (
  tasks: NMonthlyCalendar.TaskType,
  range: {
    dataS: string;
    dateE: string;
  }
): Map<string, Array<NMonthlyCalendar.UpdateTaskInterface>> => {
  const taskMap = new Map<
    string,
    Array<NMonthlyCalendar.UpdateTaskInterface>
  >();
  const rangeFormattedTasks = tasks.map((task) => ({
    ...task,
    startTime: moment(task.startTime).isBefore(moment(range.dataS))
      ? range.dataS
      : task.startTime,
    endTime: moment(task.endTime).isAfter(moment(range.dateE))
      ? range.dateE
      : task.endTime,
  }));
  const sortedTasks = sortTasksByDuration(rangeFormattedTasks);
  sortedTasks.forEach((task) => {
    const duration = getDuration(task);
    const position = findFreePosition(taskMap, task);
    for (let i = 0; i < duration; i++) {
      const currentMoment = moment(task.startTime).clone().add(i, 'days');
      const currentDay = currentMoment.date().toString();
      const currentMonth = currentMoment.month() + 1;
      const isStartDay = i === 0;
      const isEndDay = currentMoment.isSame(task.endTime, 'day');
      const isContinueDay = !isStartDay && !isEndDay;
      const updateTask: NMonthlyCalendar.UpdateTaskInterface = {
        ...task,
        startsToday: isStartDay,
        endsToday: isEndDay,
        continuesToday: isContinueDay,
        position,
      };

      if (!taskMap.has(`${currentMonth}-${currentDay}`)) {
        taskMap.set(`${currentMonth}-${currentDay}`, []);
      }
      taskMap.get(`${currentMonth}-${currentDay}`)!.push(updateTask);
    }
  });

  return taskMap;
};
export const getDurationInMilliseconds = ({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}): number => {
  const start = moment(startTime);
  const end = moment(endTime);
  return end.diff(start);
};

export const formatDailyTasks = (
  currentDate: NTaskListProvider.CurrentDate,
  tasks: Array<NCalendars.TaskInterface>
): NCalendars.DailyTasks => {
  const { year, month, day } = currentDate;
  const startOfDay = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').startOf(
    'day'
  );
  const endOfDay = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').endOf('day');

  const fullDayTasks: NCalendars.DailyTasks['fullDayTasks'] = tasks.filter(
    (task) => {
      const taskDurationInMilliseconds = getDurationInMilliseconds({
        startTime: task.startTime,
        endTime: task.endTime,
      });

      const millisecondsIn24Hours = 24 * 60 * 60 * 1000;

      const isTaskFullDay =
        taskDurationInMilliseconds >= millisecondsIn24Hours &&
        getDuration({
          startTime: task.startTime,
          endTime: task.endTime,
        }) > 1;

      const isTaskInCurrentDay =
        moment(task.startTime).isBefore(endOfDay) &&
        moment(task.endTime).isAfter(startOfDay);

      return isTaskFullDay && isTaskInCurrentDay;
    }
  );

  const dailyTaskList: NCalendars.DailyTasks['dailyTaskList'] = tasks
    .filter((task) => {
      const isTaskInCurrentDay =
        moment(task.startTime).isBefore(endOfDay) &&
        moment(task.endTime).isAfter(startOfDay);

      return !fullDayTasks.includes(task) && isTaskInCurrentDay;
    })
    .map((task) => {
      const taskStart = moment(task.startTime).isBefore(startOfDay)
        ? startOfDay
        : moment(task.startTime);
      const taskEnd = moment(task.endTime).isAfter(endOfDay)
        ? endOfDay
        : moment(task.endTime);

      const top = (taskStart.diff(startOfDay, 'minutes') / 15) * 18;
      const height =
        taskEnd.diff(taskStart, 'minutes') / 15 >= 1
          ? (taskEnd.diff(taskStart, 'minutes') / 15) * 18
          : 18;

      return {
        ...task,
        top,
        height,
        left: '4px',
      };
    });
  dailyTaskList.forEach((task, index) => {
    const overlappingTasks = dailyTaskList.filter(
      (t, i) =>
        i !== index &&
        ((t.top >= task.top && t.top < task.top + task.height) ||
          (task.top >= t.top && task.top < t.top + t.height))
    );

    const leftPercent =
      (100 / (overlappingTasks.length + 1)) * overlappingTasks.length;
    task.left = `calc(${leftPercent}% + 4px)`;

    overlappingTasks.forEach((overlappingTask, i) => {
      overlappingTask.left = `calc(${
        (100 / (overlappingTasks.length + 1)) * i
      }% + 4px)`;
    });
  });

  return { day: { year, month, day }, fullDayTasks, dailyTaskList };
};

export const getFirstDayInCurrentWeek = (
  startTime: string,
  endTime: string,
  startOfWeek: string,
  endOfWeek: string
) => {
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const startOfWeekMoment = moment(startOfWeek).startOf('day');
  const endOfWeekMoment = moment(endOfWeek).endOf('day');

  if (endMoment.isSame(startOfWeekMoment, 'day')) {
    return startOfWeekMoment.format('YYYY-MM-DD');
  }

  if (startMoment.isBefore(startOfWeekMoment)) {
    if (endMoment.isBefore(startOfWeekMoment)) {
      return null;
    }
    return startOfWeekMoment.format('YYYY-MM-DD');
  }

  if (startMoment.isAfter(endOfWeekMoment)) {
    return null;
  }

  return startMoment.format('YYYY-MM-DD');
};

export const getTaskDaysInCurrentWeek = (
  startTime: string,
  endTime: string,
  startOfWeek: string,
  endOfWeek: string
): number => {
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const startOfWeekMoment = moment(startOfWeek).startOf('day');
  const endOfWeekMoment = moment(endOfWeek).endOf('day');

  const firstDayInCurrentWeek = startMoment.isBefore(startOfWeekMoment)
    ? startOfWeekMoment
    : startMoment.startOf('day');

  const lastDayInCurrentWeek = endMoment.isAfter(endOfWeekMoment)
    ? endOfWeekMoment
    : endMoment.startOf('day');

  const daysInCurrentWeek =
    lastDayInCurrentWeek.diff(firstDayInCurrentWeek, 'days') + 1;

  return daysInCurrentWeek;
};
